import useMutation from '@hooks/useGQLMutation'
import {
  InitiateCallDocument,
  type InitiateCallMutation,
  type InitiateCallMutationVariables,
} from '@frontend/graphql/mutations/initiateCall.generated'
import { useAlertsStore } from '@stores/alertsStore'
import { FormDialog } from '@components/molecules/FormDialog'

import { scopedTranslation } from '@frontend/utils/I18n'
import type { InitiateCallDialogState } from '../../types/dialogTypes'
import { useEffect } from 'react'
import { useDialog } from '@components/DialogProvider'

const t = scopedTranslation('components.initiate_call_dialog')

const ALERTS_PATH = 'initiateCall' as const
const LOG_CONTACT_ALERTS_PATH = 'logContact' as const

type InitiateCallDialogProps = {
  onClose: () => void
  dialogState: InitiateCallDialogState
}

export function InitiateCallDialog({ onClose, dialogState }: InitiateCallDialogProps) {
  const { replace } = useDialog()
  const { dialogData } = dialogState
  const supporterId = dialogData.id
  const supporterName = `${dialogData.firstName || ''} ${dialogData.lastName || ''}`.trim()
  const groupId = dialogData.groupId
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  useEffect(() => {
    setAlerts({
      alertsPath: ALERTS_PATH,
      alerts: [
        {
          type: 'info',
          details: {
            title: t('alerts.info.title'),
          },
        },
      ],
    })
  }, [])

  const initiateCallMutationHook = useMutation(InitiateCallDocument)

  const onSubmit = () => {
    clearAlerts(ALERTS_PATH)

    const mutationData: InitiateCallMutationVariables = {
      supporterId,
      groupId,
    }

    initiateCallMutationHook.mutate(mutationData, {
      onSuccess: (response: InitiateCallMutation) => {
        if (response?.initiateCall?.success) {
          setAlerts({
            alertsPath: LOG_CONTACT_ALERTS_PATH,
            alerts: [
              {
                type: 'success',
                details: {
                  title: t('alerts.success.title', { name: supporterName }),
                },
              },
            ],
          })
          replace({
            type: 'logContact',
            dialogData: {
              id: supporterId,
              groupId,
              firstName: dialogData.firstName,
              lastName: dialogData.lastName,
              isPhoneCall: true,
              callListPersonId: dialogData.callListPersonId,
            },
          })
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts:
              response?.initiateCall?.errors?.map((error) => ({
                type: 'error',
                details: {
                  title: error.message,
                },
              })) || [],
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [
            {
              type: 'error',
              details: {
                title: t('errors.initiate_call_failed'),
              },
            },
          ],
        })
      },
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSubmit()
  }

  return (
    <FormDialog.Root open={true} onOpenChange={handleOpenChange} testId="initiate-call-dialog" preventOutsideClick>
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: supporterName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit}>
        <FormDialog.Body>
          <p>{t('body', { name: supporterName })}</p>
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('form.start_call')} isLoading={initiateCallMutationHook.isLoading} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
